// @flow
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { animateScroll as scroll } from 'react-scroll';
import { defineMessages, FormattedMessage, intlShape } from 'react-intl';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Banner from '../../../elements/Banner/Banner';
import Loading from '../../../elements/Loading/Loading';
import classNames from 'classnames/bind';
import type { Question } from '../../../elements/FreqQuestions/FreqQuestions';
// import FreqQuestions from '../../../elements/FreqQuestions/FreqQuestions';
import AlertContainer from '../../alert/container/AlertContainer';
import { FormValidator } from '../../../utils/form';
import PhoneForm from '../../preferences/security/components/PhoneForm/PhoneForm';
import Modal from '../../../elements/Modal/Modal';
import ReserveSummary, { ReserveSummaryLoading } from './ReserveSummary/ReserveSummary';
import moment from 'moment/moment';
import { getLocalizedCountry } from '../../local-values/utils/countries';
import update from 'immutability-helper';
import VerificationModalContainer from '../../auth/auth-modals/verification-modal/container/VerificationModalContainer';
import { groupItems } from '../../new-order/QuoteStep/utils';
import analytics from '../../../analytics';
import ReportModalContainer from '../../report/container/ReportModalContainer';
import FloatingBottom from '../../../elements/FloatingBottom/FloatingBottom';

import style from './Reserve.scss';
import boxStyle from '../../../assets/styles/box.scss';
import icons from '../../../assets/styles/icons.scss';
import buttonStyle from '../../../assets/styles/button.scss';
import Product, { ProductLoading } from '../../../elements/Product/Product';

import type { CountryFull, Flight, Order, OrderItem, User } from '../../../entities';
import type { HTTPError } from '../../../error';
import type { Match } from '../../../router';
import type { Option } from '../../../elements/Select/Select';
import type { LocalValuesState } from '../../local-values/duck';
import type { ItemsByStore } from '../../new-order/QuoteStep/components/QuoteSummary/QuoteSummary';
import type { LocaleState } from '../../locale/duck';
import type { FormValidationResult } from '../../../utils/form';
import type { Motive, MotiveKey } from '../../report/duck';

type Props = {
  +isFetchingFlight?: boolean;
  +flight?: Flight;
  +fetchFlightError?: HTTPError;
  +fetchFlight: (flightId: string) => void;
  +isFetchingOrder?: boolean;
  +order?: Order;
  +fetchOrder: (flightId: string, orderId: string) => void;
  +fetchOrderError?: HTTPError;
  +isPostingReserve?: boolean;
  +match: Match;
  +postReserve: (requestFlightId: string, requestOrderId: string) => void;
  +postReserveError?: HTTPError;
  +resetAlertErrorState: () => void;
  +resetState: () => void;
  +editSuccessful?: boolean;
  +postPhone: (phone: string, country: string, enableSmsNotifications: boolean) => void;
  +user?: User;
  +phoneCountry?: CountryFull;
  +isPuttingPhone?: boolean;
  +putPhoneError?: HTTPError;
  +locale: LocaleState;
  +localValues: LocalValuesState;
  +motives?: { [key: MotiveKey]: Motive[] };
};

type State = {
  country: {
    value: string;
    errors?: string[];
  };
  phone: {
    value: string;
    errors?: string[];
  };
  phoneModalVisible: boolean;
  verificationModalVisible: boolean;
  agreeToReceiveSMS: boolean;
  itemsOpen: { [key: string]: boolean };
  reportModalVisible: boolean;
};

const messages = defineMessages({
  title: {
    id: 'reserve.document.title',
  },
  description: {
    id: 'reserve.document.description',
  },
  reminderSingleHour: {
    id: 'reserve.reminder.single',
  },
  reminderLimit: {
    id: 'reserve.reminder.limit',
  },
  reminderHour: {
    id: 'reserve.reminder.hour',
  },
  reminderHours: {
    id: 'reserve.reminder.hours',
  },
  estimatedKgWeight: {
    id: 'reserve.estimated.kg',
  },
  estimatedGWeight: {
    id: 'reserve.estimated.g',
  },
});

const questionsIntlMap = {
  first: {
    title: 'reserve.faq.first.title',
    content: 'reserve.faq.first.content',
  },
  second: {
    title: 'reserve.faq.second.title',
    content: 'reserve.faq.second.content',
  },
  third: {
    title: 'reserve.faq.third.title',
    content: 'reserve.faq.third.content',
  },
  fourth: {
    title: 'reserve.faq.fourth.title',
    content: 'reserve.faq.fourth.content',
  },
  fifth: {
    title: 'reserve.faq.fifth.title',
    content: 'reserve.faq.fifth.content',
  },
};

/*const questions: Question[] = Object.keys(questionsIntlMap).map((key: string) => ({
  labelIntl: questionsIntlMap[key].title,
  contentIntl: questionsIntlMap[key].content,
}));*/

export default class Reserve extends Component<Props, State> {
  hasScrolledTop: boolean = false;
  formValidatorPhone: FormValidator;
  flightId: string;
  orderId: string;

  constructor(props: Props) {
    super(props);
    const { match, user, phoneCountry } = props;
    this.formValidatorPhone = new FormValidator({
      country: {
        required: 'required',
      },
      phone: {
        required: 'required',
      },
    });
    this.flightId = match.params.idFlight;
    this.orderId = match.params.idOrder;
    // NOTE: By default, the first one is opened
    this.state = {
      phone: { value: user ? user.phone : '' },
      country: { value: phoneCountry ? phoneCountry.code : '' },
      phoneModalVisible: false,
      verificationModalVisible: false,
      agreeToReceiveSMS: false,
      itemsOpen: {
        '0': true,
      },
      reportModalVisible: false,
    }
  }

  componentDidMount() {
    const { fetchOrder, fetchFlight, order, flight, localValues } = this.props;
    if (this.flightId && this.orderId) {
      if (!order) fetchOrder(this.flightId, this.orderId);
      if (!flight) fetchFlight(this.flightId);
    }
    if (order) {
      this.fireEvent();
    }
  }

  componentWillUnmount() {
    const { resetAlertErrorState, resetState } = this.props;
    resetAlertErrorState();
    resetState();
  }

  /*componentWillReceiveProps(props: Props) {
    const { isPostingReserve, postReserveError } = props;

    if (postReserveError && !this.hasScrolledTop) {
      this.scrollAnimateTop();
      this.hasScrolledTop = true;
    }

    if (isPostingReserve) {
      this.hasScrolledTop = false;
    }
  }*/

  componentDidUpdate(prevProps: Props) {
    const { user, editSuccessful, postReserveError, order } = this.props;
    if (!prevProps.postReserveError && !!postReserveError) {
      this.scrollAnimateTop();
    }

    if (user && prevProps.user) {
      if (!prevProps.editSuccessful && editSuccessful) {
        this.handleClickNext();
        this.handleClosePhoneModal();
      }

      if (!prevProps.user.isVerified && user.isVerified) {
        this.handleClickNext();
        this.handleCloseVerificationModal();
      }
    }

    if (!prevProps.order && !!order) {
      this.fireEvent();
    }
  }

  fireEvent() {
    const { order, localValues, locale } = this.props;

    if (order) {
      analytics.fireCustomEvent('Available Order Viewed', {
        category: 'Available Order',
        action: 'Viewed',
        label: this.orderId,
        user_type: 'traveler',
        user_referrer: window.localStorage.getItem('userReferrer') || '',
        landing_url: window.localStorage.getItem('landingUrl') || '',
        flight_id: this.flightId,
        order_id: this.orderId,
        order_formatted_id: this.orderId,
        products: analytics.mapProducts(order.items, localValues.categoriesMap),
        currency: 'USD',
        value: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.total) : 0,
        domain_country: locale.domainCountry,
        subtotal: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.subtotal) : 0,
        commission: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.commission) : 0,
        shipping: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.shipping) : 0,
        tax: order.invoice && order.invoice.detail ? parseFloat(order.invoice.detail.taxes) : 0,
        discount: order.invoice && order.invoice.detail ? Math.abs(parseFloat(order.invoice.detail.coupon)) : 0,
        coupon: order.couponCode || '',
      });
    }

  }

  handleChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState({ [name]: { value } });
    }
  }

  handleFocus(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const { name, value } = e.target;
      this.setState((state: State) => ({ [name]: update(state[name], { value: { $set: value } }) }));
    }
  }

  handleChangeCountry(option: Option) {
    this.setState({
      country: {
        value: `${option.value}`,
      },
    });
  }

  handleSubmitPhone(e: Event) {
    e.preventDefault();
    const { postPhone } = this.props;
    const phoneValue = this.state.phone.value;
    const countryValue = this.state.country.value;
    const errors = this.formValidatorPhone.validateForm({
      phone: phoneValue,
      country: countryValue,
    });
    if (errors.length) {
      errors.forEach((error: FormValidationResult) =>
        this.setState({ [error.field]: { ...this.state[error.field], errors: error.errors } }));
    } else {
      postPhone(phoneValue, countryValue, this.state.agreeToReceiveSMS);
    }
  }

  handleAcceptReceiveSMS = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      this.setState((state: State) => ({
        agreeToReceiveSMS: !state.agreeToReceiveSMS,
      }));
    }
  };

  handleClosePhoneModal() {
    this.setState({ phoneModalVisible: false });
  }

  handleCloseVerificationModal() {
    this.setState({ verificationModalVisible: false });
  }

  handleCloseReportModal = () => {
    this.setState({ reportModalVisible: false });
  };

  handleOpenReportModal = () => {
    this.setState({ reportModalVisible: true });
  };

  handleClickNext = () => {
    const { match, postReserve, user } = this.props;
    if (match.params && user) {
      if (!user.isVerified) {
        this.setState({
          verificationModalVisible: true,
        });
        return;
      }
      if (!user.phone) {
        this.setState({
          phoneModalVisible: true,
        });
        return;
      }
      postReserve(match.params.idFlight, match.params.idOrder,);
    }
  };

  handleOrderOpen = (index: number) => {
    this.setState((state: State) => {
      return {
        itemsOpen: update(state.itemsOpen, {
          [`${index}`]: {
            $set: !state.itemsOpen[`${index}`],
          },
        }),
      }
    });
  };

  scrollAnimateTop = () =>
    scroll.scrollTo(0, {
      duration: 400,
    });

  buildProducts = () => {
    const { itemsOpen } = this.state;
    const { localValues, order, locale } = this.props;
    let idx = -1;
    const itemsByStores: ?ItemsByStore[] = order && groupItems(order.items);
    return (
      <Fragment>
        <section className={style.productsTitle}>
          <FormattedMessage id="reserve.summary.title">{
            txt => <h2>{txt}</h2>
          }</FormattedMessage>
        </section>
        {itemsByStores && itemsByStores.map((store: ItemsByStore, n: number) =>
          <section key={n}>
            <FormattedMessage id="reserve.products.store">{
              txt => <span className={style.storeInfoLabel}>{txt}:</span>
            }</FormattedMessage>
            {store.logo ? <img className={style.storeImage}
                               src={store.logo}
                               alt={store.name}/> : <span>{store.name}</span>}
            {!store.items.some((item: OrderItem) => item.shipping !== '0.00') &&
            <FormattedMessage id="reserve.products.free">{
              txt => <span className={style.free}>
                  <i className={icons.symbolCheck}/>{txt}</span>
            }</FormattedMessage>}
            {store.items.map((item: OrderItem) => {
              idx++;
              return <Product key={idx}
                              index={idx}
                              item={item}
                              open={!!itemsOpen[`${idx}`]}
                              isLoading={false}
                              handleOpen={this.handleOrderOpen}
                              localeState={locale}
                              categoriesMap={localValues.categoriesMap}
                              type={'VIEW'}
                              onlineStore={item.onlineStore}
                              userType="traveler"
                              showVolumeTier={true}/>;
            })}
          </section>)}
      </Fragment>
    );
  };

  formatFlightTitle = (flight: Flight, formatMessage: (value: { [id: string]: string }) => string): string => {
    const { localValues } = this.props;
    const l = getLocalizedCountry(flight.departureCountry.code, formatMessage, localValues.countriesMap);
    const r = getLocalizedCountry(flight.arrivalCountry.code, formatMessage, localValues.countriesMap);
    return `${l} - ${r}, `;
  };

  formatBuyingHours = (hoursLimit: number): string => {
    /*const hours = Math.floor(hoursLimit).toString().padStart(2, '0');
    const minutes = (hoursLimit % 1 * 60).toString().padStart(2, '0');
    return `${hours}:${minutes}`*/
    return moment.duration(hoursLimit, 'hours').humanize();
  };

  render() {
    const {
      order, flight, match, isPostingReserve, isPuttingPhone, localValues, isFetchingOrder, motives,
      locale,
    } = this.props;
    const { country, phone, phoneModalVisible, verificationModalVisible, reportModalVisible, agreeToReceiveSMS } = this.state;
    const hoursLimit = this.formatBuyingHours(order ? parseFloat(order.buyingHours) : 0);
    const idFlight = match.params ? match.params.idFlight : '';
    const rightElement = <FormattedMessage id={'banner.order'}>{
      txt => <p>{txt} {match.params ? match.params.idOrder : ''}</p>
    }</FormattedMessage>;
    const isLoading = !order || isFetchingOrder;
    const options = motives ? motives.available.map((motive: Motive) => ({
      value: motive.name,
      label: motive.translations[locale.locale.toUpperCase()],
    })) : [];
    return (
      <Fragment>
        <Helmet key="helmet">
          <title>{this.context.intl.formatMessage(messages.title)}</title>
          <meta name="description" content={this.context.intl.formatMessage(messages.description)}/>
        </Helmet>
        <Banner key="banner"
                color="orange"
                titleElement={<FormattedMessage id="banner.back">{
                  txt => <h1>
                    <Link to={`/flight/${idFlight}/requests`}><i className={icons.iconChevronLeftMedium}/>
                      {txt}</Link>
                  </h1>
                }</FormattedMessage>}
                rightElement={rightElement}/>
        <section key="reserveWrap"
                 className={style.reserveWrap}>
          <AlertContainer showId="reserve"/>
          <section className={style.reserveTopWrap}>
            <section className={style.left}>
              <section className={classNames(boxStyle.box, style.reservationTimeSection)}>
                <FormattedMessage id="reserve.time.limit.title">
                  {txt => <h2 className={classNames(boxStyle.boxTitle, boxStyle.smallSpace)}>{txt}</h2>}
                </FormattedMessage>
                <div className={style.reservationTimer}>{hoursLimit}</div>
                <FormattedMessage id="reserve.time.limit.message">
                  {txt => <p>{txt}</p>}
                </FormattedMessage>
              </section>
              {isLoading ? [0, 1, 2].map((_, n: number) => <ProductLoading key={n}/>) : this.buildProducts()}
            </section>
            <aside>
              <FormattedMessage id="reserve.aside.title">{
                text => <h2 className={style.asideTitle}>{text}</h2>
              }</FormattedMessage>
              {isLoading || !order ?
                <ReserveSummaryLoading/> : (<Fragment>
                  <ReserveSummary order={order}/>
                </Fragment>)}
              <FormattedMessage id="reserve.button.reserve">{
                text => <button disabled={isLoading}
                                onClick={this.handleClickNext}
                                className={classNames(buttonStyle.btn, buttonStyle.orange)}>{text}</button>
              }</FormattedMessage>
              <FormattedMessage id="reserve.summary.report">{
                txt => <button disabled={isLoading}
                               className={classNames(buttonStyle.btn, buttonStyle.redInverse)}
                               onClick={this.handleOpenReportModal}>{txt}</button>
              }</FormattedMessage>
            </aside>
          </section>
          <FloatingBottom offSetY={152}>
            <section className={style.buttonsWrap}>
              <FormattedMessage id="reserve.button.reserve">{
                text => <button disabled={isLoading}
                                onClick={this.handleClickNext}
                                className={classNames(buttonStyle.btn, buttonStyle.orange)}>{text}</button>
              }</FormattedMessage>
              <FormattedMessage id="reserve.summary.report">{
                txt => <button disabled={isLoading}
                               className={classNames(buttonStyle.btn, buttonStyle.redInverse, style.report)}
                               onClick={this.handleOpenReportModal}>{txt}</button>
              }</FormattedMessage>
            </section>
          </FloatingBottom>
        </section>
        <Modal key="modal"
               open={phoneModalVisible}
               handleClose={this.handleClosePhoneModal.bind(this)}>
          <section className={style.modal}>
            <FormattedMessage id="reserve.modal.phone.required">{
              txt => <h2>{txt}</h2>
            }</FormattedMessage>
            <PhoneForm handleChangeCountry={this.handleChangeCountry.bind(this)}
                       handleChange={this.handleChange.bind(this)}
                       handleFocus={this.handleFocus.bind(this)}
                       handleSubmitPhone={this.handleSubmitPhone.bind(this)}
                       handleAcceptReceiveSMS={this.handleAcceptReceiveSMS}
                       isPuttingPhone={isPuttingPhone}
                       country={country}
                       phone={(phone: any)}
                       color="orange"
                       agreeToReceiveSMS={agreeToReceiveSMS}
                       localValues={localValues}/>
          </section>
        </Modal>
        <VerificationModalContainer key="verifier"
                                    modalVisible={verificationModalVisible}
                                    showCloseButton={true}
                                    color="orange"
                                    handleClose={this.handleCloseVerificationModal.bind(this)}/>
        <Modal handleClose={() => undefined}
               open={isPostingReserve}
               transparent
               maskColor="white"
               showCloseButton={false}>
          <Loading type="circle"
                   className={style.loading}/>
        </Modal>
        <ReportModalContainer color="orange"
                              reportType="AVAILABLE_ORDERS"
                              isOpen={reportModalVisible}
                              handleClose={this.handleCloseReportModal}
                              isDiy={order ? order.isDiy : false}
                              id={order ? order.id : ""}
                              options={options}
                              successIntl="modal.report.reserve.success.text"
                              successPath={`/flight/${match.params.idFlight}/requests`}/>
      </Fragment>
    );
  }
}

Reserve.propTypes = {
  fetchFlight: PropTypes.func.isRequired,
  fetchOrder: PropTypes.func.isRequired,
  isFetchingFlight: PropTypes.bool,
  flight: PropTypes.object,
  fetchFlightError: PropTypes.object,
  isFetchingOrder: PropTypes.bool,
  order: PropTypes.object,
  fetchOrderError: PropTypes.object,
  isPostingReserve: PropTypes.bool,
  match: PropTypes.object.isRequired,
  postReserve: PropTypes.func.isRequired,
  postReserveError: PropTypes.object,
  resetAlertErrorState: PropTypes.func.isRequired,
  resetState: PropTypes.func.isRequired,
  editSuccessful: PropTypes.bool,
  postPhone: PropTypes.func.isRequired,
  user: PropTypes.object,
  isPuttingPhone: PropTypes.bool,
  putPhoneError: PropTypes.object,
  locale: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  motives: PropTypes.object,
};

Reserve.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/reserve/components/Reserve.jsx